import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./SalesWatcher.module.css";
import { fetchRecentSales, type RecentSale } from "../lib/sales";
import { playChime } from "../lib/sound";
import { formatNok } from "../format";
import { leggTilVarsel } from "../lib/notifications";

const SEEN_KEY = "gb-seen-sales";
const POLL_MS = 120_000;

function loadSeen(): Set<string> {
  try {
    const raw = localStorage.getItem(SEEN_KEY);
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
}
function saveSeen(seen: Set<string>) {
  try {
    // behold de siste 500 for å unngå ubegrenset vekst
    const arr = [...seen].slice(-500);
    localStorage.setItem(SEEN_KEY, JSON.stringify(arr));
  } catch {
    /* ignorer */
  }
}

/**
 * Overvåker nye salg på tvers av porteføljen og feirer dem 🎉.
 * Første gang seedes eksisterende salg stille (ingen spam); deretter
 * feires kun salg som dukker opp etterpå. Dedupe via localStorage.
 */
export function SalesWatcher() {
  const [queue, setQueue] = useState<RecentSale[]>([]);
  const seededRef = useRef(false);

  useEffect(() => {
    let live = true;
    const seen = loadSeen();
    const firstEver = !localStorage.getItem(SEEN_KEY);

    async function poll() {
      try {
        const sales = await fetchRecentSales(90);
        if (!live) return;
        const fresh = sales.filter((s) => !seen.has(s.id));
        if (fresh.length) {
          fresh.forEach((s) => seen.add(s.id));
          saveSeen(seen);
          // Ved aller første lasting: feir kun de 3 nyeste (unngå spam),
          // seed resten stille. Deretter feires alle nye salg.
          const celebrate =
            firstEver && !seededRef.current ? fresh.slice(0, 3) : fresh;
          if (celebrate.length) {
            setQueue((qu) => [...qu, ...celebrate]);
            // Logg til varslingssenteret (bjella) i tillegg til popup-feiringen.
            celebrate.forEach((s) =>
              leggTilVarsel({
                id: `sale:${s.id}`,
                type: "salg",
                tittel: "Nytt salg 🎉",
                tekst: `${s.prosjekt}${s.enhet ? ` · ${s.enhet}` : ""}${
                  s.pris ? ` · ${formatNok(s.pris)}` : ""
                }`,
                lenke: `/projects/${s.projectId}`,
                tid: s.solgt ? Date.parse(s.solgt) || Date.now() : Date.now(),
              })
            );
          }
        }
        seededRef.current = true;
      } catch {
        /* stille – varsling er ikke kritisk */
      }
    }

    void poll();
    const t = setInterval(poll, POLL_MS);
    return () => {
      live = false;
      clearInterval(t);
    };
  }, []);

  if (queue.length === 0) return null;
  const current = queue[0];
  const dismiss = () => setQueue((qu) => qu.slice(1));

  return (
    <SaleCelebration
      key={current.id}
      sale={current}
      remaining={queue.length - 1}
      onClose={dismiss}
    />
  );
}

function SaleCelebration({
  sale,
  remaining,
  onClose,
}: {
  sale: RecentSale;
  remaining: number;
  onClose: () => void;
}) {
  const navigate = useNavigate();

  useEffect(() => {
    playChime();
    const t = setTimeout(onClose, 9000);
    return () => clearTimeout(t);
  }, [onClose]);

  return (
    <div className={styles.wrap} role="status" aria-live="polite">
      <Confetti />
      <div
        className={styles.card}
        onClick={() => {
          navigate(`/projects/${sale.projectId}`);
          onClose();
        }}
        title="Gå til prosjektet"
      >
        <button
          type="button"
          className={styles.close}
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          aria-label="Lukk"
        >
          ×
        </button>
        <div className={styles.icon}>🎉</div>
        <div className={styles.title}>Nytt salg!</div>
        <div className={styles.project}>{sale.prosjekt}</div>
        <div className={styles.detail}>
          {sale.enhet ? `Enhet ${sale.enhet}` : "Enhet"}
          {sale.m2 ? ` · ${Math.round(sale.m2)} m²` : ""}
          {sale.pris ? ` · ${formatNok(sale.pris)}` : ""}
        </div>
        {sale.solgt && <div className={styles.date}>Solgt {sale.solgt}</div>}
        {remaining > 0 && (
          <div className={styles.more}>+{remaining} flere nye salg</div>
        )}
      </div>
    </div>
  );
}

function Confetti() {
  // Kategorifargene (--kat-*) følger paletten
  const colors = ["blue", "teal", "magenta", "grey", "cyan"].map((t) => `var(--kat-${t})`);
  const pieces = Array.from({ length: 44 }, (_, i) => {
    const left = Math.random() * 100;
    const delay = Math.random() * 0.4;
    const duration = 1.6 + Math.random() * 1.2;
    const size = 6 + Math.random() * 6;
    return (
      <span
        key={i}
        className={styles.confetti}
        style={{
          left: `${left}%`,
          animationDelay: `${delay}s`,
          animationDuration: `${duration}s`,
          background: colors[i % colors.length],
          width: `${size}px`,
          height: `${size * 0.5}px`,
        }}
      />
    );
  });
  return (
    <div className={styles.confettiField} aria-hidden="true">
      {pieces}
    </div>
  );
}
